import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const GA_MEASUREMENT_ID = 'G-5DE8QCH4PG';

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
    TENANT_ID: string | null;
  }
}

/**
 * Hook to initialize GA4 with tenant_id and track SPA route changes.
 * Must be called once at app root level with the resolved tenant_id.
 */
export function useGoogleAnalytics(tenantId: string | null) {
  const location = useLocation();
  const isConfigured = useRef(false);
  const previousPath = useRef<string | null>(null);

  // Configure GA with tenant_id on first load
  useEffect(() => {
    if (!tenantId || isConfigured.current) return;

    // Set global tenant_id for other components to use
    window.TENANT_ID = tenantId;

    // Configure GA4 with tenant_id - this fires the initial page_view
    if (typeof window.gtag !== 'function') return;
    window.gtag('config', GA_MEASUREMENT_ID, {
      tenant_id: tenantId,
      send_page_view: true
    });

    isConfigured.current = true;
    previousPath.current = location.pathname;

    console.log('[GA] Configured with tenant_id:', tenantId);
  }, [tenantId, location.pathname]);

  // Track SPA route changes
  useEffect(() => {
    if (!isConfigured.current || !tenantId) return;
    
    // Skip if this is the initial load (already tracked by config)
    if (previousPath.current === null) {
      previousPath.current = location.pathname;
      return;
    }

    // Skip if path hasn't changed
    if (previousPath.current === location.pathname) return;

    // Fire page_view event for SPA navigation
    if (typeof window.gtag !== 'function') return;
    window.gtag('event', 'page_view', {
      page_path: location.pathname,
      page_location: window.location.href,
      page_title: document.title,
      tenant_id: tenantId
    });

    previousPath.current = location.pathname;

    console.log('[GA] Page view:', location.pathname, 'tenant_id:', tenantId);
  }, [location.pathname, tenantId]);
}

/**
 * Get the GA4 client ID for retargeting purposes.
 * Returns the client_id from the _ga cookie, or null if not available.
 */
export function getGaClientId(): string | null {
  try {
    const gaCookie = document.cookie
      .split('; ')
      .find(row => row.startsWith('_ga='));
    
    if (!gaCookie) return null;
    
    // _ga cookie format: GA1.1.XXXXXXXXXX.XXXXXXXXXX
    // We want the last two parts joined: XXXXXXXXXX.XXXXXXXXXX
    const parts = gaCookie.split('.');
    if (parts.length >= 4) {
      return `${parts[2]}.${parts[3]}`;
    }
    return null;
  } catch (error) {
    console.warn('[GA] Error getting client_id:', error);
    return null;
  }
}

/**
 * Track a custom GA4 event with tenant_id automatically included.
 * Use this for all custom events (lead forms, clicks, etc.)
 */
export function trackEvent(
  eventName: string,
  eventParams: Record<string, any> = {}
) {
  const tenantId = window.TENANT_ID;

  if (!tenantId) {
    console.warn('[GA] Cannot track event - tenant_id not set:', eventName);
    return;
  }

  if (typeof window.gtag !== 'function') return;
  window.gtag('event', eventName, {
    ...eventParams,
    tenant_id: tenantId
  });

  console.log('[GA] Event:', eventName, { ...eventParams, tenant_id: tenantId });
}

/**
 * Pre-defined event trackers for common actions
 */
export const gaEvents = {
  // Lead generation - form submitted
  generateLead: (formType: string, additionalParams?: Record<string, any>) => {
    trackEvent('generate_lead', {
      form_type: formType,
      ...additionalParams
    });
  },

  // Form started - user began filling out a form
  formStart: (formType: string, pagePath?: string) => {
    trackEvent('form_start', {
      form_type: formType,
      page_path: pagePath
    });
  },

  // Phone click
  clickCall: (phoneNumber?: string) => {
    trackEvent('click_call', {
      phone_number: phoneNumber
    });
  },

  // Email click
  clickEmail: (email?: string) => {
    trackEvent('click_email', {
      email_address: email
    });
  },

  // View listing
  viewListing: (listingId: string, listingAddress?: string) => {
    trackEvent('view_listing', {
      listing_id: listingId,
      listing_address: listingAddress
    });
  },

  // View area/neighborhood
  viewArea: (areaId: string, areaName: string) => {
    trackEvent('view_area', {
      area_id: areaId,
      area_name: areaName
    });
  },

  // View town
  viewTown: (townId: string, townName: string) => {
    trackEvent('view_town', {
      town_id: townId,
      town_name: townName
    });
  },

  // View blog post
  viewBlog: (blogId: string, blogTitle: string, blogCategory: string, blogSlug: string) => {
    trackEvent('view_blog', {
      blog_id: blogId,
      blog_title: blogTitle,
      blog_category: blogCategory,
      blog_slug: blogSlug
    });
  },

  // View blog index page
  viewBlogIndex: (page: number, categoryFilter?: string, tagFilter?: string, postCount?: number) => {
    trackEvent('view_blog_index', {
      page,
      category_filter: categoryFilter || 'all',
      tag_filter: tagFilter || 'none',
      post_count: postCount
    });
  },

  // View offer page (market value report, community guide, etc.)
  viewOffer: (offerType: string, pagePath: string) => {
    trackEvent('view_offer', {
      offer_type: offerType,
      page_path: pagePath
    });
  },

  // Search
  search: (searchTerm: string, searchType?: string) => {
    trackEvent('search', {
      search_term: searchTerm,
      search_type: searchType
    });
  }
};
